import { suppliesStoreConstants } from './constants';
import { shopifyAxios } from './shopify-axios';

type ShopifyGraphQLResponse = {
  data: {
    collection: {
      products: {
        edges: Array<{
          node: {
            id: string;
            title: string;
            images?: {
              edges: Array<{
                node: {
                  url: string;
                  altText?: string;
                };
              }>;
            };
            featuredImage?: {
              url: string;
              altText?: string;
            };
            category?: {
              id: string;
              name: string;
            };
            variants: {
              edges: Array<{
                node: {
                  id: string;
                  title: string;
                  price: {
                    amount: string;
                    currencyCode: string;
                  };
                  compareAtPrice?: {
                    amount: string;
                    currencyCode: string;
                  };
                  availableForSale: boolean;
                };
              }>;
            };
          };
        }>;
      };
    };
  };
  errors?: Array<{ message: string }>;
};

export type SuppliesProduct = {
  id: string;
  title: string;
  featuredImageUrl: string;
  featuredImageAlt: string;
  secondaryImageUrl?: string;
  secondaryImageAlt?: string;
  variantId: string;
  variantTitle: string;
  price: string;
  compareAtPrice?: string;
  currencyCode: string;
  availableForSale: boolean;
  categoryId: string;
  categoryName: string;
};

const flattenProducts = (graphqlResponse: ShopifyGraphQLResponse): SuppliesProduct[] => {
  const products = graphqlResponse?.data?.collection?.products?.edges ?? [];

  return products.map(({ node }) => {
    const variant = node.variants?.edges?.[0]?.node;

    return {
      id: node.id,
      title: node.title,
      featuredImageUrl: node.featuredImage?.url ?? '',
      featuredImageAlt: node.featuredImage?.altText ?? node.title,
      secondaryImageUrl: node.images?.edges?.[1]?.node.url,
      secondaryImageAlt: node.images?.edges?.[1]?.node.altText,
      variantId: variant?.id ?? '',
      variantTitle: variant?.title ?? '',
      price: variant?.price?.amount ?? '0.00',
      compareAtPrice: variant?.compareAtPrice?.amount,
      currencyCode: variant?.price?.currencyCode ?? 'USD',
      availableForSale: variant?.availableForSale ?? false,
      categoryId: node.category?.id ?? '',
      categoryName: node.category?.name ?? '',
    };
  });
};

export const fetchSuppliesProducts = async (): Promise<SuppliesProduct[]> => {
  const response = await shopifyAxios.post<ShopifyGraphQLResponse>(
    suppliesStoreConstants.SHOPIFY_GRAPHQL_URL,
    { query: suppliesStoreConstants.FETCH_PRODUCTS_QUERY },
    { headers: { 'Content-Type': 'application/json' } },
  );

  if (response.data.errors) {
    throw new Error(`GraphQL Error: ${JSON.stringify(response.data.errors)}`);
  }

  return flattenProducts(response.data);
};
